<template>
  <!-- @slot 控制器的插槽 -->
  <slot :isShownState="isShownState" />

  <template v-if="$slots.popover && !hidden">
    <BodyPortal :active="fixed">
      <template
        v-if="keepMounted ? true : isShownState"
      >
        <NewBsBox
          v-show="keepMounted ? isShownState : true"
          ref="popperRef"
          :bs="{
            'cmp.popover': 'wrapper',
            zIndex: stack,
            ...bs,
            ...statelessBs,
          }"
          v-bind="$attrs"
          @mouseenter="handleHover"
          @mouseleave="handleHoverLeave"
        >
          <!-- @slot 弹层的插槽 -->
          <slot
            name="popover"
            :toggle="toggle"
          />
          <NewBsBox
            v-if="showArrow"
            class="popper-arrow"
            data-popper-arrow
            :bs="{ 'cmp.popover': 'arrow', ...arrowBs }"
          />
        </NewBsBox>
      </template>
    </BodyPortal>
  </template>
</template>

<script lang="ts">
  import {
    defineComponent, watchEffect,
    PropType,
    getCurrentInstance,
    onMounted,
    onUpdated,
  } from 'vue'
  import type {
    Placement,
    } from '@popperjs/core'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'

  import { Position } from '../../constants/index'
  import { BeerStyleObj } from '../../../core'
  import {
    usePopover,
    usePopper,
    watchGlobalClick,
  } from '../../../compose/popover'
  import BodyPortal from '../../primitive/BodyPortal'
  import initStyle from './style'

  initStyle()

  function filterComment(child) {
    return child.el.nodeType !== 8
  }

  /**
   * 寻找 slot 里面的第一个 DOM element。。没有什么好可靠的判断 so far..
   * it just works, don't touch
   * TODO: add smoke test.
   */
  function lookupEl(tree: any): HTMLElement {
    // 如果 第一个 slot 里面还有嵌套了一个 slot 。。。
    if (Array.isArray(tree?.dynamicChildren?.[0]?.children)) {
      return tree
        ?.dynamicChildren.filter?.(filterComment)[0]
        ?.children.filter?.(filterComment)[0]
        ?.el
    }
    return tree
      ?.children?.filter?.(filterComment)[0]
      ?.children?.filter?.(filterComment)[0]
      ?.el || tree
        ?.children?.filter?.(filterComment)[0]
        ?.el
  }

  type Trigger = 'hover' | 'click' | 'manual' | 'focus'

  /**
   * 计算定位，控制悬浮窗体的组件
   *
   * 扩展组件：
   * @see Tooltip /beer-next/ui-blocks/overlayers/tooltip-doc
   * @see BubbleConfirm /beer-next/ui-blocks/overlayers/bubble-doc
   */
  export default defineComponent({
    name: 'Popover',
    components: {
      NewBsBox,
      BodyPortal,
    },
    inheritAttrs: false,
    props: {
      /**
       * 是否显示，在 trigger = manual 时使用，用于外部状态控制显示
       */
      isShown: {
        type: Boolean,
        required: false,
      },
      /**
       * 是否显示三角箭头指向对象
       */
      showArrow: {
        type: Boolean,
        default: true,
      },
      /**
       * 浮层的 index
       */
      stack: {
        type: Number,
        default: 800,
      },
      /**
       * @ignore
       *  三角箭头的额外样式
       */
      arrowBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 在弹层显示后的回调
       */
      onOpen: {
        type: Function as PropType<() => void>,
        default: () => ({}),
      },
      /**
       * 在弹层显示前的回调，返回 true 表示阻止打开
       */
      onBeforeOpen: {
        type: Function as PropType<() => boolean>,
        default: () => false,
      },
      /**
       * 在弹层关闭后的回调
       */
      onClose: {
        type: Function as PropType<() => void>,
        default: () => ({}),
      },
      /**
       * 是否挂载在 Body 元素后
       */
      fixed: {
        type: Boolean,
        default: true,
      },
      /**
       * 在 trigger = hover 时是否自动关闭
       */
      shouldCloseOnHoverLeave: {
        type: Boolean,
        default: true,
      },
      /**
       * 是否在点击外部元素后自动关闭
       */
      shouldCloseOnExternalClick: {
        type: Boolean,
        default: true,
      },
      /**
       * 触发方式
       * @type {click|hover|manual|focus}
       */
      trigger: {
        type: String as PropType<Trigger>,
        default: 'click',
      },
      /**
       * Hover 关闭的延时，避免过于敏感的抖动
       */
      hideDelay: {
        type: Number,
        default: 50,
      },
      /**
       * 弹层显示的方向，从 constants 模块中获取 enum
       */
      placement: {
        type: String as PropType<Placement>,
        default: Position.BOTTOM_LEFT,
      },
      /**
       * 弹出浮层的样式
       * @ignore
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * popper 用 v-show 而不是 v-if 控制
       */
      keepMounted: {
        type: Boolean,
        required: false,
      },
      /**
       * @ignore
       * @deprecated
       * 用 bs 即可
       */
      statelessBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否弹层宽度和对应target 一样宽
       */
      sameWidth: {
        type: Boolean,
        default: false,
      },
      /**
       * 弹层宽度和对应target 一样宽的时候额外宽度
       */
      sameWidthPad: {
        type: Number,
        default: 0,
      },
      offsetX: {
        type: Number,
        default: 0,
      },
      /**
       * 是否临时禁用 Popover 功能，在外部条件判断显示时有用
       */
      hidden: {
        type: Boolean,
        default: false,
      },
    },
    setup(props) {
      const {
        handleClick,
        handleHover,
        handleCloseHover,
        handleHoverLeave,
        isShownState,
        toggle,
      } = usePopover({
        onBeforeOpen: props.onBeforeOpen,
        onOpen: props.onOpen,
        onClose: props.onClose,
        trigger: props.trigger,
        hideDelay: props.hideDelay,
        shouldCloseOnHoverLeave: props.shouldCloseOnHoverLeave,
      })

      const { targetRef, popperRef } = usePopper(
        isShownState,
        {
          placement: props.placement,
          strategy: props.fixed ? 'fixed' : 'absolute',
          sameWidth: props.sameWidth,
          sameWidthPad: props.sameWidthPad,
          offsetX: props.offsetX,
        },
      )

      function ensureTarget() {
        const internalInstance = getCurrentInstance()
        if (!targetRef.value) {
          // @ts-ignore
          const targetEl = lookupEl(
            internalInstance
              ?.subTree
              ?.dynamicChildren
              ?.find(i => i.key === '_default'),
          )
          if (targetEl) {
            if (props.trigger === 'click') {
              targetEl.addEventListener('click', handleClick)
            }
            if (props.trigger === 'hover') {
              targetEl.addEventListener('mouseenter', handleHover)
              targetEl.addEventListener('mouseleave', handleCloseHover)
            }
            if (props.trigger === 'focus') {
              targetEl.addEventListener('focusin', () => toggle(true))
              targetEl.addEventListener('focusout', () => toggle(false))
            }
            targetRef.value = targetEl
          }
        }
      }

      onMounted(ensureTarget)
      onUpdated(ensureTarget)

      watchEffect(() => {
        if (props.trigger === 'manual') {
          isShownState.value = props.isShown
          toggle(props.isShown)
        }
      })

      if (props.shouldCloseOnExternalClick) {
        watchGlobalClick({
          targetRef,
          popperRef,
          toggle,
          isShownState,
        })
      }

      return {
        toggle,
        popperRef,
        handleClick,
        handleHover,
        handleCloseHover,
        handleHoverLeave,
        isShownState,
      }
    },
  })
</script>
